
import { Component, Vue } from 'vue-property-decorator';
import { BlockStore, CommonStore } from '@/store/modules';
import { Block } from '@/store/models/block';

import SvgIcon from '@/components/SvgIcon.vue';
import ImportBlockForm from '@/components/Forms/Blocks/ImportBlockForm.vue';
import { QTablePagination } from '@/store/models/meta';
import helpers from '@/utils/helpers';

import { colors } from 'quasar';
const { getPaletteColor } = colors;

@Component({
  components: {
    SvgIcon,
    ImportBlockForm
  }
})
export default class BlockList extends Vue {
  block: Block | null = null;

  deleteDialog = false;
  loading = false;
  generatingFarmBlocks = false;
  generatingBlockPolygon: number[] = [];
  filter = '';

  tableColumn = [
    {
      name: 'id',
      label: 'Id',
      field: (row: Block) => row.id,
      sortable: true,
      align: 'left',
      style: 'width: 10px',
      headerStyle: 'width: 10px;'
    },
    {
      name: 'name',
      label: 'Name',
      field: (row: Block) => row.name,
      sortable: true,
      align: 'left'
    },
    {
      name: 'farm__owner__first_name',
      label: 'Owner',
      field: (row: Block) => row.ownerName,
      sortable: true,
      align: 'left'
    },
    {
      name: 'farm__name',
      label: 'Farm',
      field: (row: Block) => row.farmName,
      sortable: true,
      align: 'left'
    },
    {
      name: 'crop__name',
      label: 'Crop',
      field: (row: Block) => `${row.cropName} (${row.varietyName})`,
      sortable: true,
      align: 'left'
    },
    {
      name: 'current_sensor_location',
      label: 'Sensor Location',
      field: (row: Block) => row.currentSensorLocation,
      sortable: true,
      align: 'left'
    },
    {
      name: 'current_sensor',
      label: 'Sensor',
      field: (row: Block) => row.currentSensor,
      sortable: true,
      align: 'left'
    },
    {
      name: 'area',
      label: 'Area (ha)',
      field: (row: Block) => row.area,
      sortable: true,
      align: 'left'
    },
    {
      name: 'nRows',
      label: 'Rows',
      field: (row: Block) => row.nRows,
      sortable: true,
      align: 'left'
    },
    {
      name: 'nTrees',
      label: 'Trees',
      field: (row: Block) => row.nTrees,
      sortable: true,
      align: 'left'
    },
    {
      name: 'r2r',
      label: 'Row to Row',
      field: (row: Block) => row.r2r,
      sortable: true,
      align: 'left'
    },
    {
      name: 't2t',
      label: 'Tree to Tree',
      field: (row: Block) => row.t2t,
      sortable: true,
      align: 'left'
    },
    {
      name: 'loRows',
      label: 'Length of Rows',
      field: (row: Block) => row.loRows,
      sortable: true,
      align: 'left'
    },
    {
      name: 'actions',
      label: 'Actions',
      align: 'left',
      style: 'width: 260px;'
    }
  ];
  get currentFarm() {
    return CommonStore.SelectedFarmBlock;
  }
  get blocks() {
    return BlockStore.Blocks;
  }

  get blockPagination() {
    return BlockStore.BlockTablePagination;
  }

  set blockPagination(data: QTablePagination) {
    BlockStore.setBlockTablePagination(data);
  }

  get importBlockDialogVisible() {
    return BlockStore.ImportBlockDialogVisible;
  }
  set importBlockDialogVisible(value: boolean) {
    BlockStore.setImportBlockDialogVisible(value);
  }

  //eslint-disable-next-line
  async onRequest(props: any) {
    const { page, rowsPerPage, sortBy, descending } = props.pagination;
    const filter = props.filter;
    this.loading = true;

    const ordering = `${descending ? '-' : ''}${sortBy}`;

    const queryParams = {
      page: page,
      search: filter,
      ordering: ordering,
      pageSize: rowsPerPage,
      farm: this.currentFarm.farmId
    };
    await BlockStore.getAllMyBlocks(queryParams);
    this.loading = false;

    this.blockPagination = {
      page: this.blocks.currentPage,
      rowsPerPage: this.blocks.pageSize,
      rowsNumber: this.blocks.totalObjects,
      sortBy: sortBy,
      descending: descending
    };
  }

  async exportFarmBlocks() {
    this.generatingFarmBlocks = true;
    const response = await BlockStore.exportFarmBlocks(this.currentFarm.farmId);
    this.generatingFarmBlocks = false;
    if (response.url) {
      const link = document.createElement('a');
      link.setAttribute('href', response.url);
      link.click();
    } else {
      this.$q.notify({
        message: "Could not export farm's block",
        color: 'negative',
        icon: 'warning'
      });
    }
  }

  async exportBlock(id: number) {
    this.generatingBlockPolygon.push(id);
    const response = await BlockStore.exportBlock(id);
    this.generatingBlockPolygon.splice(
      this.generatingBlockPolygon.indexOf(id),
      1
    );

    if (response.url) {
      const link = document.createElement('a');
      link.setAttribute('href', response.url);
      link.click();
    } else {
      this.$q.notify({
        message: "Could not export farm's block",
        color: 'negative',
        icon: 'warning'
      });
    }
  }

  getPaletteColor(colorName: string) {
    return getPaletteColor(colorName);
  }

  async onDelete(blockId: number) {
    this.deleteDialog = false;
    if (blockId != 0) {
      BlockStore.deleteBlock(blockId);

      BlockStore.setAddEditBlock({} as Block);
    }
  }

  deleteBlock(block: Block) {
    this.block = block;
    this.deleteDialog = true;
  }

  editBlock(block: Block) {
    this.block = block;
    BlockStore.setAddEditBlock(block);
  }

  loadBlocks() {
    this.onRequest({
      pagination: this.blockPagination,
      filter: this.filter
    });
  }

  async created() {
    this.loading = true;
    await helpers.fetchingDefaultFarm();
    this.loadBlocks();
    this.loading = false;
  }
}
